import { cloneDeep } from 'lodash';
import { batch } from 'react-redux';
import { EmeraldError } from 'src/common/api/emerald-error';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import { EntryMethod } from 'src/common/enumerations/entry-method';
import { TenderType } from 'src/common/enumerations/tender-type';
import { TenderAmountExceededEligibilityAmount } from 'src/common/helpers/error-code';
import { TenderingActions } from 'src/common/redux/reducers/tendering-reducer';
import { CartResponse } from 'src/items-list/business-logic/service/interfaces/cart-response';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import { ITenderAdd, PaymentUsage, RefundUsage } from '../../common/interfaces/dtos/IPreTenderAdd';
import { store } from '../../common/redux/configure-store';
import { TenderMenuActions } from '../../common/redux/reducers/tender-menu-reducer';
import { applyCartResponse } from './common/apply-cart-response';
import { preRefundAdd } from './service/pre-refund-add';
import { preRefundDelete } from './service/pre-refund-delete';
import { preTenderAdd } from './service/pre-tender-add';

export const preMakeTenderPayment = async (lineTender: ITenderAdd): Promise<void> => {
  return withSpinner(() => doPreTenderLogic(lineTender));
};

const doPreTenderLogic = async (lineTender: ITenderAdd): Promise<void> => {
  const balanceDue = store.getState().itemList.amountDue;
  let promiseToRun;

  if (lineTender?.tender?.cancel) {
    if (balanceDue < 0) {
      promiseToRun = cancelPreRefundLine;
    } else {
      promiseToRun = cancelPreTenderLine;
    }
  } else {
    if (balanceDue < 0) {
      promiseToRun = callPreRefundAdd;
    } else {
      promiseToRun = addPreTenderLine;
    }
  }

  if (lineTender.tender?.processedByEps) {
    return promiseToRun(lineTender).catch((err: EmeraldError) => {
      if (err.code === TenderAmountExceededEligibilityAmount) {
        const maxAmount = err?.data?.eligibility?.maxEligibleAmount;
        if (maxAmount) {
          const newLineTender = cloneDeep(lineTender);
          if (newLineTender.amount?.amount) {
            newLineTender.amount.amount = maxAmount;
          }
          // TODO handle pre-refund separately???
          store.dispatch(TenderMenuActions.setUnPaidPreTender(newLineTender));
          return callPreTenderAdd(newLineTender);
        }
      }
      throw err;
    });
  } else {
    return promiseToRun(lineTender);
  }
};

const cancelPreTenderLine = async (lineTender: ITenderAdd): Promise<void> => {
  if (store.getState().tenderMenu.unPaidPreTender) {
    return callPreTenderAdd(lineTender);
  }
};

const cancelPreRefundLine = async (lineTender: ITenderAdd): Promise<void> => {
  const transactionId = store.getState().transaction.transactionId;
  if (store.getState().tenderMenu.unPaidPreTender) {
    const cartResponse = await preRefundDelete(transactionId, lineTender);
    updatePreTender(cartResponse, lineTender);
  }
};

const addPreTenderLine = async (lineTender: ITenderAdd): Promise<void> => {
  const unPaidPreTender = store.getState().tenderMenu.unPaidPreTender;
  if (unPaidPreTender) {
    if (preTenderLinesAreEqual(unPaidPreTender, lineTender)) {
      return;
    }
    await cancelUnpaidPreTender(unPaidPreTender);
  }

  return callPreTenderAdd(lineTender);
};

const callPreRefundAdd = async (lineTender: ITenderAdd): Promise<void> => {
  const transactionId = store.getState().transaction.transactionId;
  if (transactionId) {
    lineTender.typeCode =
      lineTender.tender?.usage === PaymentUsage.ExchangeReceive
        ? RefundUsage.ExchangeReceive
        : RefundUsage.Refund;

    await preRefundAdd(transactionId, lineTender, {}).then((cartResponse: CartResponse): void => {
      setEwicData(cartResponse);
      updatePreTender(cartResponse, lineTender);
    });
  }
};

const callPreTenderAdd = async (lineTender: ITenderAdd): Promise<void> => {
  const transactionId = store.getState().transaction.transactionId;
  if (transactionId) {
    await preTenderAdd(transactionId, lineTender).then(cartResponse => {
      setEwicData(cartResponse);
      updateAmountWhenRoundedAmountNeeded(cartResponse, lineTender);
      updatePreTender(cartResponse, lineTender);
      return applyCartResponse(cartResponse);
    });
  }
};

const preTenderLinesAreEqual = (unPaidPreTender: ITenderAdd, lineTender: ITenderAdd): boolean => {
  const { preTenderFullEquality } = getCurrentXLRConfig();

  if (preTenderFullEquality) {
    return (
      unPaidPreTender.tender?.tenderId === lineTender.tender?.tenderId &&
      unPaidPreTender.amount?.preTenderAmount === lineTender.amount?.preTenderAmount &&
      unPaidPreTender.amount?.amount === lineTender.amount?.amount &&
      unPaidPreTender.epsData?.authorization?.authorizationCode ===
        lineTender.epsData?.authorization?.authorizationCode &&
      unPaidPreTender.amount?.requestedAmount === lineTender.amount?.requestedAmount
    );
  } else {
    return (
      unPaidPreTender.tender?.tenderId === lineTender.tender?.tenderId &&
      unPaidPreTender.epsData?.authorization?.authorizationCode ===
        lineTender.epsData?.authorization?.authorizationCode &&
      unPaidPreTender.amount?.amount === lineTender.amount?.amount
    );
  }
};

const updatePreTender = (preTenderResult: CartResponse, tenderLinesToAdd: ITenderAdd): void => {
  const preTenderLineId = tenderLinesToAdd.typeCode
    ? preTenderResult.refunds?.find(refunds => refunds.status === 'preTender')?.sequenceNumber
    : preTenderResult.payments?.find(payment => payment.status === 'preTender')?.sequenceNumber;

  const pretender = { ...tenderLinesToAdd, lineId: preTenderLineId };
  store.dispatch(TenderMenuActions.clearUnPaidPreTender());
  if (!pretender.tender?.cancel) {
    store.dispatch(TenderMenuActions.setUnPaidPreTender(pretender));
  }
};

const cancelUnpaidPreTender = async (unPaidPreTender: ITenderAdd): Promise<void> => {
  const lineTenderToCancel = {
    ...unPaidPreTender,
    tender: { ...unPaidPreTender.tender, cancel: true },
  };
  return callPreTenderAdd(lineTenderToCancel).then(() => {
    store.dispatch(TenderMenuActions.clearUnPaidPreTender());
  });
};

export const setEwicData = (response: CartResponse): void => {
  const { requiresGetTenderFromCart } = getCurrentXLRConfig();

  const wicTender = response.payments?.find(
    tender =>
      tender.tenderType?.toLowerCase() === TenderType.Ewic.toLowerCase() &&
      tender.status === 'preTender',
  );
  const ewicEpsUtilizedAmountTotal = requiresGetTenderFromCart
    ? wicTender?.amount
    : response?.ewicEpsUtilizedAmountTotal;
  if (response?.ewicEpsUtilizedAmountTotal) {
    store.dispatch(
      TenderingActions.setEwicEpsUtilizedAmountTotal(response.ewicEpsUtilizedAmountTotal),
    );
  }
  batch(() => {
    if (response?.ewicReceipts?.length) {
      store.dispatch(TenderingActions.setEwicReceipts(response.ewicReceipts));
    }
    if (ewicEpsUtilizedAmountTotal) {
      store.dispatch(
        TenderingActions.setEwicEpsUtilizedAmountTotal(ewicEpsUtilizedAmountTotal ?? 0),
      );
    }
  });
};

const updateAmountWhenRoundedAmountNeeded = (
  response: CartResponse,
  lineTender: ITenderAdd,
): void => {
  if (lineTender.amount?.amountEntryMethod !== EntryMethod.Manual) {
    const roundedAmount = response?.cartInfo?.totals?.roundedBalanceDueAmount;
    if (roundedAmount && lineTender?.amount?.amount) {
      lineTender.amount.amount = roundedAmount;
    }
  }
};
